import { useQuery } from '@tanstack/react-query';

import { fetchEscritorios, type EscritorioResumo, type OrigemDaVisao } from '../api/client';
import { fetchSupervisionados } from '../api/compartilhamento';
import { useAuth } from './useAuth';
import { useAtuacao } from '../lib/delegacao/atuacao';
import { queryPersistMeta } from '../lib/pwa/queryPersistence';

/**
 * A visão do escritório vale aqui? (docs/PLANO_COMPARTILHAMENTO_ESCRITORIO.md A7.)
 *
 * Só para quem é membro de algum escritório e fora da delegação: o que foi
 * compartilhado com o titular é da pessoa dele, não de quem atua por ele. Sem
 * escritório, as listas ficam exatamente como eram (sem filtro de origem).
 */
export function useVisaoDoEscritorio(): boolean;
export function useVisaoDoEscritorio(opcoes: { comEstado: true }): { ativa: boolean; conhecida: boolean };
export function useVisaoDoEscritorio(opcoes?: { comEstado: true }) {
  const atuacao = useAtuacao();
  // A mesma query de escritórios de Documentos e Modelos (``['escritorios']``),
  // persistida: offline e no cold start a visão é conhecida sem esperar a rede.
  const { data, isFetched, isError } = useQuery({
    queryKey: ['escritorios'],
    queryFn: fetchEscritorios,
    meta: queryPersistMeta.nonEmpty,
  });
  const ativa = !atuacao && (data?.length ?? 0) > 0;
  if (!opcoes?.comEstado) return ativa;
  // Na delegação não importa; sem resposta (erro, ou offline sem cache) segue sem a visão.
  return { ativa, conhecida: !!atuacao || data !== undefined || isFetched || isError };
}

/**
 * O filtro do acervo do gestor: "Meus" (o de todo dia — o que é dele, o que cuida e o que
 * compartilharam com ele) ou "Todo o escritório" (o acervo de todos, só para ler). Quem não
 * é gestor não tem o filtro: vê sempre o seu.
 */
export const OPCOES_DE_ORIGEM: { value: OrigemDaVisao; label: string }[] = [
  { value: 'escritorio', label: 'Todo o escritório' },
];

/** É Proprietário ou gestor de algum escritório (vê "Todo o escritório"). */
export function useSouGestor(): boolean {
  const atuacao = useAtuacao();
  const { data } = useQuery<EscritorioResumo[]>({
    queryKey: ['escritorios'],
    queryFn: fetchEscritorios,
    meta: queryPersistMeta.nonEmpty,
    staleTime: 5 * 60 * 1000,
  });
  return !atuacao && (data ?? []).some((e) => e.sou_gestor || e.meu_role === 'PROPRIETARIO');
}

/**
 * Quem o usuário pode "visualizar como" (A8, Bloco 6): a rota devolve vazio para quem não
 * é gestor, então basta ser membro de algum escritório. Não se filtra pelo `sou_gestor` da
 * lista em cache: a marca de Gestor é dada por OUTRA pessoa, e a lista (5 min, persistida)
 * seguia dizendo que não era — o seletor não aparecia. A rota é da pessoa, então vale
 * também enquanto ele está na conta de outro (é por ela que o contexto se confere).
 */
export function useSupervisionados() {
  const { user } = useAuth();
  const escritorios = useQuery({
    queryKey: ['escritorios'],
    queryFn: fetchEscritorios,
    meta: queryPersistMeta.nonEmpty,
  });
  const membro = (escritorios.data?.length ?? 0) > 0;
  return useQuery({
    queryKey: ['escritorios', 'supervisionados', user?.id ?? null],
    queryFn: fetchSupervisionados,
    enabled: !!user?.id && membro,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: true,
  });
}
